const DEG_TO_RAD = Math.PI / 180;

function finite(value, label) {
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error(`Radial preview requires ${label}`);
  return number;
}

function rotate2D(point, yawDeg) {
  const radians = yawDeg * DEG_TO_RAD;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return {
    x: cosine * point.x - sine * point.y,
    y: sine * point.x + cosine * point.y,
  };
}

function distance(left, right) {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

function triangle(target, a, b, c) {
  target.push(...a, ...b, ...c);
}

function footprintSpanAtY(footprint, y) {
  const sourceMinimumY = footprint[0].y;
  const sourceMaximumY = footprint[3].y;
  const t = (y - sourceMinimumY) / (sourceMaximumY - sourceMinimumY);
  return {
    left: { x: footprint[0].x + (footprint[3].x - footprint[0].x) * t, y },
    right: { x: footprint[1].x + (footprint[2].x - footprint[1].x) * t, y },
  };
}

function footprintBand(footprint, minimumY, maximumY) {
  const low = footprintSpanAtY(footprint, minimumY);
  const high = footprintSpanAtY(footprint, maximumY);
  return [low.left, low.right, high.right, high.left];
}

function appendPrism(structurePositions, floorPositions, footprint, baseZ, topZ, { walkableTop = false } = {}) {
  const bottom = footprint.map((vertex) => [vertex.x, baseZ, vertex.y]);
  const top = footprint.map((vertex) => [vertex.x, topZ, vertex.y]);
  triangle(structurePositions, bottom[0], bottom[1], bottom[2]);
  triangle(structurePositions, bottom[0], bottom[2], bottom[3]);
  for (let index = 0; index < 4; index += 1) {
    const next = (index + 1) % 4;
    triangle(structurePositions, bottom[index], top[index], top[next]);
    triangle(structurePositions, bottom[index], top[next], bottom[next]);
  }
  const topTarget = walkableTop ? floorPositions : structurePositions;
  triangle(topTarget, top[0], top[3], top[2]);
  triangle(topTarget, top[0], top[2], top[1]);
}

/**
 * Keep road intent visibly distinct from the tunnel itself. These line
 * segments are a review-only planning proxy: they never add mesh faces,
 * material slots, collision, or Unreal export instructions.
 */
function buildRoadGuideModel(opening, footprint, clearMinimumY, clearMaximumY, floorTopZ) {
  const roadLayout = opening?.roadLayout;
  if (!roadLayout) return null;

  const laneCount = finite(roadLayout.laneCount, "longitudinalOpening.roadLayout.laneCount");
  if (laneCount !== 2 && laneCount !== 3) throw new Error("Radial preview road layout requires two or three lanes");
  const laneWidthM = finite(roadLayout.laneWidthM, "longitudinalOpening.roadLayout.laneWidthM");
  const shoulderWidthM = finite(roadLayout.shoulderWidthM, "longitudinalOpening.roadLayout.shoulderWidthM");
  const medianWidthM = finite(roadLayout.medianWidthM, "longitudinalOpening.roadLayout.medianWidthM");
  if (laneWidthM <= 0 || shoulderWidthM < 0 || medianWidthM < 0) throw new Error("Radial preview road layout has invalid widths");

  const clearRadialWidthM = clearMaximumY - clearMinimumY;
  const usedWidthM = laneCount * laneWidthM + shoulderWidthM * 2 + medianWidthM;
  if (usedWidthM > clearRadialWidthM + Number.EPSILON) throw new Error("Radial preview road layout exceeds the tunnel clearance");
  const remainingClearanceM = clearRadialWidthM - usedWidthM;
  const roadMinimumY = clearMinimumY + remainingClearanceM / 2;
  const firstDirectionLaneCount = Math.ceil(laneCount / 2);
  const segments = [];
  const addGuide = (kind, y) => {
    const span = footprintSpanAtY(footprint, y);
    segments.push({
      kind,
      startM: { x: span.left.x, y: floorTopZ + 0.035, z: y },
      endM: { x: span.right.x, y: floorTopZ + 0.035, z: y },
    });
  };

  let cursorY = roadMinimumY + shoulderWidthM;
  if (shoulderWidthM > 0) addGuide("shoulder_boundary", cursorY);
  for (let laneIndex = 0; laneIndex < laneCount; laneIndex += 1) {
    cursorY += laneWidthM;
    if (laneIndex === laneCount - 1) break;
    if (laneIndex + 1 === firstDirectionLaneCount && medianWidthM > 0) {
      addGuide("median_edge", cursorY);
      cursorY += medianWidthM;
      addGuide("median_edge", cursorY);
    } else {
      addGuide("lane_divider", cursorY);
    }
  }
  if (shoulderWidthM > 0) addGuide("shoulder_boundary", cursorY + shoulderWidthM);

  return {
    role: "planning_proxy",
    axis: "x",
    laneCount,
    laneWidthM,
    shoulderWidthM,
    medianWidthM,
    usedWidthM,
    remainingClearanceM,
    clearRadialWidthM,
    firstDirectionLaneCount,
    segments,
  };
}

/**
 * Convert radial-structure design intent into renderer-neutral Three.js input.
 * Worldloom/Unreal X,Y,Z becomes Three X,Z,Y; Unreal yaw is therefore negated
 * when applied around Three's Y-up axis.
 */
export function buildRadialStructurePreviewModel(structure) {
  const segment = structure?.segment;
  const assembly = structure?.assembly;
  const local = segment?.localTopVerticesM;
  if (!segment || !assembly || !local) throw new Error("Radial preview geometry is incomplete");

  const count = finite(segment.count, "segment.count");
  if (!Number.isInteger(count) || count < 3) throw new Error("Radial preview requires at least three whole segments");
  const spacingDeg = finite(segment.angularSpacingDeg, "segment.angularSpacingDeg");
  const radiusM = finite(assembly.placementRadiusCm, "assembly.placementRadiusCm") / 100;
  const startAngleDeg = finite(assembly.startAngleDeg, "assembly.startAngleDeg");
  const yawOffsetDeg = finite(assembly.yawOffsetDeg, "assembly.yawOffsetDeg");
  const baseZM = finite(segment.baseZM, "segment.baseZM");
  const topZM = finite(segment.topZM, "segment.topZM");
  if (topZM <= baseZM) throw new Error("Radial preview top must be above its base");

  const vertexNames = ["innerLeft", "innerRight", "outerRight", "outerLeft"];
  const footprint = vertexNames.map((name) => ({
    x: finite(local[name]?.x, `segment.localTopVerticesM.${name}.x`),
    y: finite(local[name]?.y, `segment.localTopVerticesM.${name}.y`),
  }));
  // The first material group is the structure shell; the second is the
  // walkable top/floor. Positions are deliberately non-indexed so each face
  // receives a clean outward normal from Three.js.
  const structurePositions = [];
  const floorPositions = [];
  const opening = structure.longitudinalOpening || null;
  let roadGuides = null;
  if (opening) {
    if (opening.axis !== "x") throw new Error("Radial preview only supports a longitudinal X-axis opening");
    const floorThicknessM = finite(opening.floorThicknessM, "longitudinalOpening.floorThicknessM");
    const leftWallThicknessM = finite(opening.leftWallThicknessM, "longitudinalOpening.leftWallThicknessM");
    const rightWallThicknessM = finite(opening.rightWallThicknessM, "longitudinalOpening.rightWallThicknessM");
    const roofThicknessM = finite(opening.roofThicknessM, "longitudinalOpening.roofThicknessM");
    const minimumY = Math.min(...footprint.map((vertex) => vertex.y));
    const maximumY = Math.max(...footprint.map((vertex) => vertex.y));
    const floorTopZ = baseZM + floorThicknessM;
    const roofBottomZ = topZM - roofThicknessM;
    const clearMinimumY = minimumY + leftWallThicknessM;
    const clearMaximumY = maximumY - rightWallThicknessM;
    if (floorTopZ >= roofBottomZ || clearMinimumY >= clearMaximumY) throw new Error("Radial preview opening does not leave positive clearance");

    appendPrism(structurePositions, floorPositions, footprint, baseZM, floorTopZ, { walkableTop: true });
    appendPrism(structurePositions, floorPositions, footprint, roofBottomZ, topZM, { walkableTop: true });
    appendPrism(structurePositions, floorPositions, footprintBand(footprint, minimumY, clearMinimumY), floorTopZ, roofBottomZ);
    appendPrism(structurePositions, floorPositions, footprintBand(footprint, clearMaximumY, maximumY), floorTopZ, roofBottomZ);
    roadGuides = buildRoadGuideModel(opening, footprint, clearMinimumY, clearMaximumY, floorTopZ);
  } else {
    appendPrism(structurePositions, floorPositions, footprint, baseZM, topZM, { walkableTop: true });
  }
  const positions = [...structurePositions, ...floorPositions];
  const structureVertexCount = structurePositions.length / 3;

  const instances = Array.from({ length: count }, (_, index) => {
    const angleDeg = startAngleDeg + index * spacingDeg;
    const angleRad = angleDeg * DEG_TO_RAD;
    const yawDeg = angleDeg + yawOffsetDeg;
    const outward = rotate2D({ x: 0, y: 1 }, yawDeg);
    return {
      index,
      actorLabel: `${assembly.actorLabelPrefix || "Instance_"}${String(index).padStart(2, "0")}`,
      angleDeg,
      yawDeg,
      threeRotationYRad: -yawDeg * DEG_TO_RAD,
      positionM: { x: radiusM * Math.cos(angleRad), y: 0, z: radiusM * Math.sin(angleRad) },
      outwardAlignment: outward.x * Math.cos(angleRad) + outward.y * Math.sin(angleRad),
    };
  });

  const worldVertex = (instanceIndex, vertexIndex) => {
    const instance = instances[instanceIndex];
    const rotated = rotate2D(footprint[vertexIndex], instance.yawDeg);
    return { x: instance.positionM.x + rotated.x, y: instance.positionM.z + rotated.y };
  };
  let maximumSeamErrorCm = 0;
  for (let index = 0; index < count; index += 1) {
    const next = (index + 1) % count;
    maximumSeamErrorCm = Math.max(
      maximumSeamErrorCm,
      distance(worldVertex(index, 0), worldVertex(next, 1)) * 100,
      distance(worldVertex(index, 3), worldVertex(next, 2)) * 100,
    );
  }
  if (instances.some((instance) => instance.outwardAlignment < 1 - 1e-12)) {
    throw new Error("Radial preview local +Y axis is not outward-facing");
  }
  const seamToleranceCm = Number(structure.acceptance?.seamToleranceCm);
  if (Number.isFinite(seamToleranceCm) && maximumSeamErrorCm > seamToleranceCm + Number.EPSILON) {
    throw new Error(`Radial preview seam error ${maximumSeamErrorCm} cm exceeds ${seamToleranceCm} cm`);
  }

  return {
    structureId: structure.id,
    name: structure.name || structure.label || "Radial structure",
    sourceMeshCount: 1,
    geometry: {
      positions,
      groups: [
        { start: 0, count: structureVertexCount, materialIndex: 0 },
        { start: structureVertexCount, count: positions.length / 3 - structureVertexCount, materialIndex: 1 },
      ],
      sourceTriangles: positions.length / 9,
      sourceVertices: positions.length / 3,
    },
    instances,
    maximumSeamErrorCm,
    baseZM,
    topZM,
    radiusM,
    longitudinalOpening: opening,
    roadGuides,
  };
}

/**
 * Build the reusable source module by itself.  This deliberately does not
 * place it on the future dodecagon: callers can use the one exact mesh as the
 * thing they place or instance in a world.
 */
export function buildRadialStructureSourceSegmentPreviewModel(structure) {
  const assembledPreview = buildRadialStructurePreviewModel(structure);
  const segment = structure.segment;
  const local = segment.localTopVerticesM;

  return {
    ...assembledPreview,
    previewKind: "source_segment",
    instanceCount: 1,
    sourceMeshCount: 1,
    instances: [{
      index: 0,
      actorLabel: `${structure.unreal?.staticMeshPath?.split("/").pop() || "RadialStructureSegment"}`,
      angleDeg: 0,
      yawDeg: 0,
      threeRotationYRad: 0,
      positionM: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      outwardAlignment: 1,
    }],
    maximumSeamErrorCm: 0,
    radiusM: 0,
    sourceDimensionsM: {
      centrelineLengthM: finite(segment.centrelineLengthM, "segment.centrelineLengthM"),
      radialDepthM: finite(segment.radialDepthM, "segment.radialDepthM"),
      totalHeightM: finite(segment.totalHeightM, "segment.totalHeightM"),
      innerFaceLengthM: finite(segment.innerFaceLengthM, "segment.innerFaceLengthM"),
      outerFaceLengthM: finite(segment.outerFaceLengthM, "segment.outerFaceLengthM"),
      localBoundsM: {
        minX: Math.min(...Object.values(local).map((vertex) => finite(vertex.x, "segment.localTopVerticesM.x"))),
        maxX: Math.max(...Object.values(local).map((vertex) => finite(vertex.x, "segment.localTopVerticesM.x"))),
        minY: Math.min(...Object.values(local).map((vertex) => finite(vertex.y, "segment.localTopVerticesM.y"))),
        maxY: Math.max(...Object.values(local).map((vertex) => finite(vertex.y, "segment.localTopVerticesM.y"))),
        minZ: assembledPreview.baseZM,
        maxZ: assembledPreview.topZM,
      },
      longitudinalOpening: assembledPreview.longitudinalOpening,
      roadGuides: assembledPreview.roadGuides,
    },
  };
}
